import { useEffect, useState } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { AlertTriangle, ShieldCheck, TrendingUp, TrendingDown, CalendarRange, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportPdf } from "../lib/pdf";

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

export default function Monthly() {
  const { fmt, settings } = useFarm();
  const [data, setData] = useState({ months: [], alerts: [] });

  useEffect(() => { api.monthlyRecords().then(setData); }, []);

  const { months, alerts } = data;
  const downloadPdf = () => exportPdf({
    settings, title: "Monthly Records", filename: "monthly-records.pdf",
    sections: [
      { heading: "Monthly Ledger", columns: ["Month", "Activities", "Income", "Expenses", "Net", "Produce Sales", "Yield Qty"],
        rows: [...months].reverse().map((m) => [monthLabel(m.month), m.activity_count, { money: m.income }, { money: m.expenses }, { money: m.net }, { money: m.produce }, m.yield_qty || ""]) },
      ...(alerts.length ? [{ heading: "Irregularity Alerts", columns: ["Area", "Month", "Severity", "Message"], rows: alerts.map((a) => [a.area, monthLabel(a.month), a.severity, a.message]) }] : []),
    ],
  });
  const chart = months.map((m) => ({ name: monthLabel(m.month), Income: m.income, Expenses: m.expenses, Produce: m.produce }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <CalendarRange className="h-7 w-7 text-accent" /> Monthly Records
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Month-by-month activity and finances, with automatic irregularity alerts.</p>
        </div>
        <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={downloadPdf} disabled={!months.length} data-testid="monthly-export-pdf-button"><FileDown className="h-4 w-4" /> Export PDF</Button>
      </div>

      {/* Alerts */}
      <section>
        <h2 className="font-display text-lg font-bold tracking-tight mb-3">Irregularity Alerts</h2>
        {alerts.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="alerts-container">
            {alerts.map((a, i) => {
              const high = a.severity === "high";
              const tone = a.concerning
                ? (high ? "border-destructive/50 bg-destructive/10 text-destructive" : "border-amber-500/50 bg-amber-500/10 text-amber-600")
                : "border-[hsl(var(--emerald))]/40 bg-[hsl(var(--emerald))]/10 text-[hsl(var(--emerald))]";
              const Icon = a.direction === "up" ? TrendingUp : TrendingDown;
              return (
                <Card key={i} className={`p-4 border ${tone}`} data-testid={`alert-card-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    {a.concerning ? <AlertTriangle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    <span className="font-semibold text-sm">{a.area} · {monthLabel(a.month)}</span>
                    <Badge variant="outline" className="ml-auto capitalize text-[10px] border-current">{a.severity}</Badge>
                  </div>
                  <p className="text-sm text-foreground/90">{a.message}</p>
                  <p className="mt-1.5 text-xs text-muted-foreground font-mono-nums">
                    {fmt(a.value)} vs typical {fmt(a.baseline)} ({a.deviation_pct > 0 ? "+" : ""}{a.deviation_pct}%)
                  </p>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-6 flex items-center gap-3 text-muted-foreground" data-testid="no-alerts">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--emerald))]" />
            <span className="text-sm">No irregularities detected. Your key areas are within normal ranges.</span>
          </Card>
        )}
      </section>

      {/* Chart */}
      {chart.length > 0 && (
        <Card className="p-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Income vs Expenses vs Produce</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                formatter={(v) => fmt(v)}
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }}
              />
              <Legend />
              <Bar dataKey="Income" fill="hsl(var(--emerald))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Expenses" fill="hsl(var(--terracotta))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Produce" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Records table */}
      <Card className="overflow-hidden">
        <div className="p-5 border-b border-border">
          <h2 className="font-display text-lg font-bold tracking-tight">Monthly Ledger</h2>
        </div>
        {months.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="monthly-records-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Activities</TableHead>
                  <TableHead className="text-right">Income</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Produce Sales</TableHead>
                  <TableHead className="text-right">Yield Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...months].reverse().map((m) => (
                  <TableRow key={m.month} data-testid={`month-row-${m.month}`}>
                    <TableCell className="font-medium">{monthLabel(m.month)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{m.activity_count}</TableCell>
                    <TableCell className="text-right font-mono-nums text-[hsl(var(--emerald))]">{fmt(m.income)}</TableCell>
                    <TableCell className="text-right font-mono-nums text-[hsl(var(--terracotta))]">{fmt(m.expenses)}</TableCell>
                    <TableCell className={`text-right font-mono-nums font-semibold ${m.net >= 0 ? "" : "text-destructive"}`}>{fmt(m.net)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmt(m.produce)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{m.yield_qty || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="py-16 text-center text-sm text-muted-foreground">No monthly records yet.</p>
        )}
      </Card>
    </div>
  );
}
